import { BaseDragger } from "./BaseDragger";
import { DraggerType, type Point2D, type Point3D } from "./types";
import { PREVIEW_LINE_STYLE } from "./previewStyles";
import { insertTextEntity } from "./textInsertion";
import { Logger } from "@/logger";
import type {
  HandwritingRecognizer,
  HandwritingStatus,
  HandwritingStatusListener,
} from "../handwriting/types";

/** Idle delay after the last stroke before auto-converting, in ms. */
const IDLE_CONVERT_MS = 1000;

/** Minimum distance in pixels between collected points (same as freehand). */
const MIN_POINT_DISTANCE = 2;

/** Height of the normalized ink image sent to the recognizer, in px. */
const EXPORT_HEIGHT = 128;

/** White margin around the ink in the exported image, in px. */
const EXPORT_MARGIN = 24;

/** Pen width in the exported image, in px. */
const EXPORT_PEN_WIDTH = 6;

/** Ignore ink whose world bbox is smaller than this many screen pixels. */
const MIN_INK_SCREEN_SIZE = 6;

/**
 * Dragger for handwriting-to-text (draw2text).
 *
 * Unlike FreehandDragger, strokes are NOT committed as entities: they
 * accumulate as overlay ink (multi-stroke, so accents and t-bars work).
 * After an idle delay — or an explicit convertNow() from the toolbar —
 * the ink is rendered to a normalized image, sent to the injected
 * HandwritingRecognizer, and the recognized text is inserted as an MText
 * at the ink position, sized by the active scale config (same path as
 * TextDragger).
 *
 * Strokes are stored in world coordinates and re-projected on pan/zoom,
 * so ink stays glued to the drawing while writing.
 */
export class HandwritingDragger extends BaseDragger {
  readonly type = DraggerType.Handwriting;

  /** Completed strokes, in world coordinates. */
  private strokes: Point3D[][] = [];
  /** One overlay polyline id per stroke (same indexes as strokes). */
  private overlayIds: string[] = [];

  private currentStroke: Point3D[] | null = null;
  private currentOverlayId: string | null = null;
  private lastScreenPoint: Point2D | null = null;

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private status: HandwritingStatus = "idle";
  private lastError: string | null = null;
  private statusListeners = new Set<HandwritingStatusListener>();
  private recognizer: HandwritingRecognizer | null = null;
  private unsubscribeViewChanged: (() => void) | null = null;

  // ============== Public API (via HandwritingManager) ==============

  setRecognizer(recognizer: HandwritingRecognizer): void {
    this.recognizer = recognizer;
  }

  getStatus(): HandwritingStatus {
    return this.status;
  }

  /** Technical detail of the last failure (null when none). */
  getLastError(): string | null {
    return this.lastError;
  }

  hasInk(): boolean {
    return this.strokes.length > 0;
  }

  /** Subscribe to status changes. Returns an unsubscribe function. */
  onStatusChange(listener: HandwritingStatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  /** Convert the pending ink immediately (toolbar ✓ button). */
  convertNow(): void {
    this.cancelIdleTimer();
    void this.convert();
  }

  /** Drop the pending ink without converting. */
  discardInk(): void {
    this.cancelIdleTimer();
    this.removeInkOverlays();
    this.strokes = [];
    this.currentStroke = null;
    this.setStatus("idle");
  }

  // ============== Lifecycle ==============

  protected onActivate(): void {
    this.unsubscribeViewChanged = this.viewer.onViewChanged(() =>
      this.reprojectInkOverlays(),
    );
    this.recognizer?.warmup?.();
  }

  protected onDeactivate(): void {
    this.unsubscribeViewChanged?.();
    this.unsubscribeViewChanged = null;
    this.discardInk();
  }

  // ============== Dragger Callbacks ==============

  protected onStart(screen: Point2D, world: Point3D): void {
    if (this.status === "converting") return;

    this.cancelIdleTimer();
    this.currentStroke = [world];
    this.lastScreenPoint = screen;

    const overlay = this.viewer.overlayManager;
    if (overlay) {
      this.currentOverlayId = overlay.createPolyline(
        [screen],
        PREVIEW_LINE_STYLE,
      );
    }
    this.setStatus("inked");
  }

  protected onDrag(screen: Point2D, world: Point3D): void {
    if (!this.currentStroke) return;

    if (this.lastScreenPoint) {
      const dx = screen.x - this.lastScreenPoint.x;
      const dy = screen.y - this.lastScreenPoint.y;
      if (Math.sqrt(dx * dx + dy * dy) < MIN_POINT_DISTANCE) return;
    }

    this.currentStroke.push(world);
    this.lastScreenPoint = screen;

    const overlay = this.viewer.overlayManager;
    if (overlay && this.currentOverlayId) {
      overlay.addPointToPolyline(this.currentOverlayId, screen);
    }
  }

  protected onEnd(): void {
    this.finalizeCurrentStroke();
    if (this.strokes.length) {
      this.startIdleTimer();
    }
  }

  protected onCancel(): void {
    // A cancelled gesture (e.g. multi-touch navigation) should not lose
    // the stroke-in-progress silently; keep what was captured so far.
    this.finalizeCurrentStroke();
    if (this.strokes.length) {
      this.startIdleTimer();
    }
  }

  // ============== Stroke Management ==============

  private finalizeCurrentStroke(): void {
    if (this.currentStroke?.length) {
      this.strokes.push(this.currentStroke);
      if (this.currentOverlayId) {
        this.overlayIds.push(this.currentOverlayId);
      }
    } else if (this.currentOverlayId) {
      this.viewer.overlayManager?.remove(this.currentOverlayId);
    }
    this.currentStroke = null;
    this.currentOverlayId = null;
    this.lastScreenPoint = null;
  }

  private removeInkOverlays(): void {
    const overlay = this.viewer.overlayManager;
    if (overlay) {
      for (const id of this.overlayIds) overlay.remove(id);
      if (this.currentOverlayId) overlay.remove(this.currentOverlayId);
    }
    this.overlayIds = [];
    this.currentOverlayId = null;
  }

  /** Keep the ink glued to the drawing when the user pans/zooms. */
  private reprojectInkOverlays(): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;
    this.strokes.forEach((stroke, index) => {
      const id = this.overlayIds[index];
      if (!id) return;
      const points = stroke.map((p) => this.viewer.worldToScreen(p));
      overlay.updatePolyline(id, points);
    });
  }

  // ============== Idle Timer ==============

  private startIdleTimer(): void {
    this.cancelIdleTimer();
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      void this.convert();
    }, IDLE_CONVERT_MS);
  }

  private cancelIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  // ============== Conversion ==============

  private async convert(): Promise<void> {
    if (this.status === "converting" || !this.strokes.length) return;

    if (!this.recognizer) {
      Logger.error("HandwritingDragger: no recognizer injected");
      this.setStatus("error", "no recognizer injected");
      return;
    }

    const image = this.exportInkImage();
    if (!image) {
      this.discardInk();
      return;
    }

    this.setStatus("converting");
    try {
      const text = (await this.recognizer.recognize(image)).trim();
      if (!text) {
        Logger.info("HandwritingDragger: recognizer returned empty text");
        this.setStatus("error", "aucun texte reconnu");
        return;
      }
      this.insertText(text);
      this.discardInk();
    } catch (err) {
      Logger.error(`HandwritingDragger: recognition failed: ${err}`);
      this.setStatus("error", err instanceof Error ? err.message : String(err));
    }
  }

  /**
   * Render the ink strokes to a black-on-white PNG, cropped to the ink
   * bbox and normalized to EXPORT_HEIGHT. Rendering uses world
   * coordinates (y-up), so the result is independent of the current zoom.
   */
  private exportInkImage(): string | null {
    const points = this.strokes.flat();
    if (!points.length) return null;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of points) {
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    }

    // Reject accidental taps: bbox smaller than a few screen pixels.
    const screenMin = this.viewer.worldToScreen({ x: minX, y: minY, z: 0 });
    const screenMax = this.viewer.worldToScreen({ x: maxX, y: maxY, z: 0 });
    const screenSize = Math.max(
      Math.abs(screenMax.x - screenMin.x),
      Math.abs(screenMax.y - screenMin.y),
    );
    if (screenSize < MIN_INK_SCREEN_SIZE) return null;

    const bboxW = Math.max(maxX - minX, 1e-9);
    const bboxH = Math.max(maxY - minY, 1e-9);
    const scale = (EXPORT_HEIGHT - 2 * EXPORT_MARGIN) / bboxH;
    const width = Math.max(Math.round(bboxW * scale) + 2 * EXPORT_MARGIN, 1);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = EXPORT_HEIGHT;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "black";
    ctx.fillStyle = "black";
    ctx.lineWidth = EXPORT_PEN_WIDTH;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    for (const stroke of this.strokes) {
      // World y grows up, canvas y grows down: flip vertically.
      const toCanvas = (p: Point3D): Point2D => ({
        x: (p.x - minX) * scale + EXPORT_MARGIN,
        y: (maxY - p.y) * scale + EXPORT_MARGIN,
      });
      const first = toCanvas(stroke[0]);
      if (stroke.length === 1) {
        ctx.beginPath();
        ctx.arc(first.x, first.y, EXPORT_PEN_WIDTH / 2, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      ctx.beginPath();
      ctx.moveTo(first.x, first.y);
      for (let i = 1; i < stroke.length; i++) {
        const p = toCanvas(stroke[i]);
        ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
    }

    return canvas.toDataURL("image/png");
  }

  /**
   * Insert the recognized text as an MText at the ink position.
   * Same creation path as TextDragger: height comes from the active
   * scale config (textsize), one AddEntityCommand for clean undo.
   */
  private insertText(text: string): void {
    // Only the position comes from the ink: the text is sized like every
    // other annotation, from the active scale config (same as TextDragger),
    // so handwritten notes stay consistent with the rest of the drawing.
    const points = this.strokes.flat();
    let minX = Infinity;
    let maxY = -Infinity;
    for (const p of points) {
      minX = Math.min(minX, p.x);
      maxY = Math.max(maxY, p.y);
    }

    insertTextEntity(this.viewer, this.module, {
      via: "handwriting",
      worldX: minX,
      worldY: maxY,
      text,
      config: this.viewer.activeNoteConfig,
      source: "HandwritingDragger.insertText",
    });
  }

  // ============== Status ==============

  private setStatus(status: HandwritingStatus, detail?: string): void {
    this.lastError = status === "error" ? (detail ?? null) : null;
    if (this.status === status && status !== "error") return;
    this.status = status;
    for (const listener of this.statusListeners) {
      listener(status, detail);
    }
  }
}
